import {
  Component,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icons/icon.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { OrderWarning } from '../../core/models/ordering/order-warning.model';
import { OrderWarningType } from '../../core/models/ordering/order-warning-type.enum';
import { OrderService } from '../../core/services/order.service';
import { HypermediaService } from '../../core/services/hypermedia.service';
import { OrderSpecialService } from '../../core/services/ordering/order-special.service';
import { ModalService, ModalName, GenericModalContext } from '../../core/services/modal.service';
import { TranslocoService, TranslocoPipe } from '@ngneat/transloco';
import { map } from 'rxjs';
import { OrderableSpecial } from '../../core/models/menu/orderable-special.model';
import { DeserializationUtils } from '../../core/utils/deserialization-utils';
import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { ErrorsService } from '../../core/services/errors.service';

@Component({
  selector: 'cwo-order-warning',
  standalone: true,
  imports: [IconComponent, CommonModule, TranslocoPipe],
  templateUrl: './order-warning.component.html',
  styleUrl: './order-warning.component.scss',
})
export class OrderWarningComponent {
  iconNameType = IconNameType;
  orderWarningType = OrderWarningType;
  orderService = inject(OrderService);
  modalService = inject(ModalService);
  translocoService = inject(TranslocoService);
  order = computed(() => this.orderService.order());
  orderSpecialService = inject(OrderSpecialService);
  hypermediaService = inject(HypermediaService);
  authService = inject(AuthService);
  toastService = inject(ToastService);
  errorService = inject(ErrorsService);
  

  warning = input.required<OrderWarning>();
  borderRadius = input<string>('');
  showInstructions = input<boolean>(false);
  loading = false;
  feedbackType: 'error' | 'success' | 'none' = 'none';
  feedbackText?: string;

  accountEmail = computed(() => {
    return this.authService.state().account?.email;
  });

  readonly instructionButtonClicked = output<{
    openModal: boolean;
    warningType: string;
    modalRef?: GenericModalContext;
}>();

  handleInstruction() {
    if (this.warning().type === OrderWarningType.ORDER) {
      const errorType = this.order().pendingSpecial?.errorType;
      
      if (errorType === 'NOT_LOGGED_IN') {
        this.openLoginRequiredModal(false);
        return;
      }
      
      if (errorType === 'NOT_VERIFIED') {
        this.openLoginRequiredModal(true);
        return;
      }
    }

    if (this.warning().type === OrderWarningType.ORDER || this.warning().type === OrderWarningType.MISSING_ORDER) {
      this.instructionButtonClicked.emit({openModal: true, warningType: this.warning().type});
    }

    if (this.warning().type === OrderWarningType.SPECIAL) {
      this.hypermediaService
        .get(this.warning().special!.links, 'orderableSpecial')
        .pipe(
          map((json) =>
            DeserializationUtils.deserializeObj(json, OrderableSpecial)
          )
        )
        .subscribe({
          next: (orderableSpecial) => {
            this.orderSpecialService.updateOrderWarningSpecial(orderableSpecial);
            this.instructionButtonClicked.emit({openModal: true, warningType: this.warning().type});
          },
        });
    }
  }

  resendVerificationEmail() {
    const email = this.accountEmail();
    if (!email) {
      return;
    }
    this.loading = true;
    this.authService.resendVerificationEmail(email).subscribe({
      next: () => {
      this.loading = false;
      this.toastService.showSuccess(this.translocoService.translate('modals.verify_email.verification_email_sent'));
      },
      error: (error) => {
        this.loading = false;
        const parsedError = this.errorService.parseAllErrorsFromResponse(error);
        this.feedbackType = 'error';
        this.feedbackText = parsedError.allErrors[0];
      }
    });
  }

  private openLoginRequiredModal(isVerified: boolean) {
    this.modalService.openForcedChoiceModal({
      modalTitle: this.translocoService.translate('modals.coupon_registration_error_modal.coupon_details'),
      showCloseButton: true,
      customClass: isVerified ? 'login-required-modal verified' : 'login-required-modal',
      modalMessage: `<span class='coupon-name'> ${this.order().pendingSpecial?.name}</span> <br/> <br/> 
                      ${this.order().pendingSpecial?.instructions} 
                      ${isVerified 
                        ? this.translocoService.translate('modals.coupon_registration_error_modal.first_piece_no_verification', { coupon: this.order().pendingSpecial?.name })
                        : this.translocoService.translate('modals.coupon_registration_error_modal.first_piece', { coupon: this.order().pendingSpecial?.name })}`,
      messageParts: isVerified 
      ? undefined
      : {
        beforeLink: this.translocoService.translate('modals.coupon_registration_error_modal.message_part1'),
        linkText: this.translocoService.translate('modals.coupon_registration_error_modal.sign_up_text'),
        afterLink: this.translocoService.translate('modals.coupon_registration_error_modal.message_part2'),
        linkAction: () => {
          this.modalService.closeModal(ModalName.ForcedChoice);
          this.modalService.openModal(ModalName.SignIn, {
            activeTab: this.translocoService.translate('modals.coupon_registration_error_modal.sign_up'),
            title: this.translocoService.translate('modals.coupon_registration_error_modal.sign_up'),
            submitButton: this.translocoService.translate('modals.coupon_registration_error_modal.sign_up')
          });
        }
      },
      confirmButtonText: this.translocoService.translate(
        isVerified
        ? 'modals.coupon_registration_error_modal.resend_verification'
        : 'modals.coupon_registration_error_modal.sign_in'
      ),
      cancelButtonText: this.translocoService.translate('modals.coupon_registration_error_modal.no_thanks_button'),
      primaryAction : isVerified 
      ? () => {
        this.resendVerificationEmail();
      }
      : () => {
        this.modalService.openModal(ModalName.SignIn).subscribe()
      },
      secondaryAction: () => {
        this.modalService.closeModal();
      }
    });
  }
}
